/**
 * Validates Russian phone numbers.
 * Valid formats:
 * - Only allows digits, spaces, hyphens (-), parentheses ( ), and optional leading +
 * - Rejects any letters, cyrillic, or invalid symbols
 * - 11 digits starting with +7, 7 or 8 (e.g. +7 926 936 91 95, 8 926 936 91 95, 8 800 555 35 35)
 * - 10 digits without prefix (e.g. 9269369195 for mobiles, 812... for St. Petersburg, 495... for Moscow)
 */
export function isValidRussianPhone(value) {
    if (!value || typeof value !== "string") return false;
    const trimmed = value.trim();
    if (!trimmed) return false;

    // Disallow ANY letter, cyrillic, or forbidden character.
    // Strictly allow digits, spaces, hyphens (-), parentheses ( ), and optional leading +
    if (/[^0-9\s()+ -]/.test(trimmed)) return false;

    // If + is present, it MUST strictly be at the very start and only once
    if (trimmed.includes("+")) {
        if (!trimmed.startsWith("+")) return false;
        if (trimmed.indexOf("+", 1) !== -1) return false;
    }

    // Check parentheses validity
    const openParenCount = (trimmed.match(/\(/g) || []).length;
    const closeParenCount = (trimmed.match(/\)/g) || []).length;
    if (openParenCount !== closeParenCount || openParenCount > 1) return false;
    if (openParenCount === 1) {
        const openIdx = trimmed.indexOf("(");
        const closeIdx = trimmed.indexOf(")");
        if (openIdx > closeIdx) return false;
        // Inside parentheses should be 3 or 4 digits (area/mobile code)
        const inside = trimmed.slice(openIdx + 1, closeIdx).replace(/\D/g, "");
        if (inside.length < 3 || inside.length > 4) return false;
    }

    const digits = trimmed.replace(/\D/g, "");

    // +7 followed by 10 digits (11 digits total). (+8 is not Russian country code)
    if (trimmed.startsWith("+")) {
        return digits.length === 11 && digits.startsWith("7");
    }

    // 11 digits starting with 7 or 8 (national trunk prefix in Russia)
    if (digits.length === 11 && (digits.startsWith("7") || digits.startsWith("8"))) {
        return true;
    }

    // 10 digits local/regional format (starts with 3, 4, 8, 9)
    if (digits.length === 10 && /^[3489]/.test(digits)) {
        return true;
    }

    return false;
}

// System reserved paths and object prefixes in VK that cannot be user profiles
const VK_RESERVED_PATHS = new Set([
    "feed", "im", "mail", "friends", "groups", "photos", "audios", "videos",
    "market", "bookmarks", "settings", "support", "apps", "docs", "edit",
    "login", "logout", "privacy", "terms", "help", "search", "join", "restore",
    "wall", "topic", "photo", "video", "audio", "doc", "story", "poll", "album",
    "tag", "narrative", "clips", "services", "calls", "messenger", "pages"
]);

/**
 * Validates a VK username, screen name, or ID string (without domain).
 * - Numeric ID: "id123456" or pure digits "123456"
 * - Community ID: "club123456", "public123456", "event123456"
 * - Screen name / alias: 2-32 chars of [a-z0-9_.], cannot start/end with . or _, no consecutive .. or __
 */
function isValidVkIdentifier(handle) {
    if (!handle || typeof handle !== "string") return false;
    const clean = handle.trim().replace(/^@/, "");

    // Pure numeric ID: e.g. 263883393
    if (/^\d{1,12}$/.test(clean)) {
        return true;
    }

    // Standard user ID or community ID with prefix: id123456, club123456, public123456, event123456
    if (/^(?:id|club|public|event)\d{1,12}$/i.test(clean)) {
        return true;
    }

    // Reserved paths/words in VK
    if (VK_RESERVED_PATHS.has(clean.toLowerCase())) {
        return false;
    }

    // Wall or object links like wall-123_456, photo123_456, etc.
    if (/^(?:wall|photo|video|audio|album|topic)-?\d+_\d+$/i.test(clean)) {
        return false;
    }

    // Screen name / alias rules:
    // - 2 to 32 characters
    // - Latin letters, numbers, dot, underscore
    // - Cannot start or end with dot or underscore
    // - Cannot have consecutive dots (..) or underscores (__)
    if (!/^[a-zA-Z0-9_.]{2,32}$/.test(clean)) {
        return false;
    }

    if (clean.startsWith(".") || clean.endsWith(".") || clean.startsWith("_") || clean.endsWith("_")) {
        return false;
    }

    if (clean.includes("..") || clean.includes("__")) {
        return false;
    }

    return true;
}

/**
 * Validates VK (VKontakte) contact inputs.
 * Accepts:
 * - Direct handle: e.g. "jorgecd", "@jorgecd", "ivan.ivanov"
 * - Numeric ID: e.g. "id263883393", "263883393"
 * - Strictly VK URLs/links:
 *   - https://vk.ru/jorgecd
 *   - https://vk.ru/id263883393
 *   - https://vk.com/jorgecd
 *   - https://m.vk.com/jorgecd
 *   - https://www.vk.com/jorgecd
 *   - https://vk.me/jorgecd
 *   - vk.com/jorgecd
 *   - vk.ru/jorgecd
 *   - vk.me/jorgecd
 *   - Links with parameters/anchors (e.g. ?from=profile, #wall)
 * Rejects other domains/URLs (e.g. google.com, facebook.com, etc.)
 * and system paths (vk.com/feed, vk.com/im, etc.)
 */
export function isValidVk(value) {
    if (!value || typeof value !== "string") return false;
    const trimmed = value.trim();
    if (!trimmed) return false;

    // Disallow whitespace within handle or URL
    if (/\s/.test(trimmed)) return false;

    const hasProtocol = /^https?:\/\//i.test(trimmed);
    const hasSlash = trimmed.includes("/");

    // If it has a protocol (http/https) or slash (/), it MUST strictly be a VK URL
    if (hasProtocol || hasSlash) {
        // Accepts: vk.com, vk.ru, vk.me with optional http(s)://, optional www. or m., optional trailing slash, query, hash
        const vkUrlRegex = /^(?:https?:\/\/)?(?:(?:m|www)\.)?(?:vk\.com|vk\.ru|vk\.me)\/@?([a-zA-Z0-9_.]+)\/?(?:[?#].*)?$/i;
        const match = trimmed.match(vkUrlRegex);
        if (!match) return false;

        const handle = match[1];
        return isValidVkIdentifier(handle);
    }

    // If it looks like another website or foreign domain name (e.g. facebook.com, site.ru)
    if (/\.(?:com|ru|org|net|io|me|app|dev|co|xyz|site|online)$/i.test(trimmed)) {
        return false;
    }

    return isValidVkIdentifier(trimmed);
}
